// Shared axios instance + helpers for the PUBLIC client onboarding routes
// (prefix `client-onboarding/{token}`). The PIN header and the 401/423 interceptor
// are managed by ClientOnboarding.vue; child components only call the helpers below.
import axios from "axios";
import axiosIns from "@/libs/axios";

export const publicAxios = axios.create({ baseURL: axiosIns.defaults.baseURL });

let currentToken = "";

export function setToken(token) {
  currentToken = token || "";
}

export function getToken() {
  return currentToken;
}

function base() {
  return `client-onboarding/${currentToken}`;
}

export function getStatus() {
  return publicAxios.get(`${base()}/status`);
}

export function verifyPin(pin) {
  return publicAxios.post(`${base()}/verifyPin`, { pin });
}

export function getOnboarding() {
  return publicAxios.get(base());
}

export function saveForm(formData) {
  return publicAxios.post(`${base()}/saveForm`, { formData });
}

export function signForm(payload) {
  return publicAxios.post(`${base()}/signForm`, payload);
}

// multipart {file, requirementId?, note?}
export function uploadDocument({ file, requirementId, note }) {
  const data = new FormData();
  data.append("file", file);
  if (requirementId) data.append("requirementId", requirementId);
  if (note) data.append("note", note);
  return publicAxios.post(`${base()}/uploadDocument`, data, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

export function requestDocument({ title, note }) {
  return publicAxios.post(`${base()}/requestDocument`, { title, note: note || null });
}

// Streams the file and triggers a browser download.
export function downloadDocument(documentId, fileName) {
  return publicAxios
    .get(`${base()}/downloadDocument/${documentId}`, { responseType: "blob" })
    .then((res) => {
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName || "document";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    });
}

export function errorMessage(err, fallback) {
  return (err && err.response && err.response.data && err.response.data.message) || fallback || "Something went wrong. Please try again.";
}

export function formatSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export const requirementStatusVariant = {
  Requested: "warning",
  Submitted: "info",
  UnderReview: "info",
  Approved: "success",
  Rejected: "danger",
};

export const reviewStatusVariant = {
  Pending: "warning",
  UnderReview: "info",
  Approved: "success",
  Rejected: "danger",
  NotApplicable: "secondary",
};

export const customerRequestStatusVariant = {
  Requested: "warning",
  Fulfilled: "success",
  Declined: "danger",
};

export function statusLabel(status) {
  return (
    {
      UnderReview: "Under review",
      NotApplicable: "Not applicable",
      InProgress: "In progress",
    }[status] || status || ""
  );
}
